/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  InvoiceType,
  JobFinancialDrawPaymentDto,
  JobFinancialsDrawDto,
  JobFinancialsDto,
  JobFinancialsExpenseDto,
} from '@gh/builder-shared-data';
import { FormStateSnapshot, formStateSnapshot, getFormStateSnapshotValue } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { coalesce, valueComparator } from '@gh/core-util';

import { filter, includes, map, omit, sortBy, sumBy, toNumber } from 'lodash';

export type JobFinancialsDraw = JobFinancialsDrawDto;
export type JobFinancialsDrawTotal = JobFinancialsDrawDto & {
  amount: string;
  balance: string;
  placement: JobFinancialsInvoicePlacement;
};
export type JobFinancialsDrawPayment = JobFinancialDrawPaymentDto;
export type JobFinancialsExpense = JobFinancialsExpenseDto;
export type JobFinancialsExpenseTotals = JobFinancialsExpenseDto & {
  actualCost: number;
  budgetCost: number;
  variance: string;
  placement: JobFinancialsExpensePlacement;
};

export type JobFinancials = {
  jobInfo: FormStateSnapshot<JobFinancialsInfo>;
  invoices: JobFinancialsDraw[];
  customerId?: ID;
  jobExpenses: JobFinancialsExpense[];
  revisedBudget?: number;
  contractAmount?: number;
  variationsAmount?: number;
  invoicesAmount?: number;
  reportUrl?: string;
};

export type JobFinancialsInfo = {
  jobId?: ID;
  jobNumber?: number;
  customerName?: string;
  customerId?: ID;
  siteAddress?: string;
};

export const jobFinancialsFromDto = (dto: JobFinancialsDto): JobFinancials => ({
  jobInfo: formStateSnapshot({
    jobId: dto.jobId,
    jobNumber: dto.jobNumber,
    siteAddress: dto.siteAddress,
    customerName: dto.customerName,
    customerId: dto.customerId,
  }),
  invoices: sortBy(map(dto.invoices, (invoice: JobFinancialsDrawDto) => ({
    ...invoice,
    invoiceNumber: invoice.invoiceNumber,
    payments: sortBy(invoice.payments, 'reference'),
  })), 'accountingDate'), // tslint:disable-line:align
  customerId: dto.customerId,
  jobExpenses: sortBy(dto.jobExpenses, 'costCenterCode'),
  revisedBudget: dto.revisedBudget,
  contractAmount: dto.contractAmount,
  variationsAmount: dto.variationsAmount,
  invoicesAmount: dto.invoicesAmount,
  reportUrl: dto.reportUrl,
});

export type JobFinancialsTradingAccount = {
  revisedBudget: number;
  lessTotalInvoices: number;
  variance: number;
  estimatedProfit: number;
  actualProfitToDate: number;
  originalContract: number;
  plusVariations: number;
  currentContractPrice: number;
  lessClaimsPaid: number;
  balanceToReceive: number;
};

export const jobFinancialsToDto = (jobFinancials: JobFinancials): JobFinancialsDto => ({
  ...getFormStateSnapshotValue(jobFinancials.jobInfo),
  ...<JobFinancialsDto>omit(jobFinancials, ['jobInfo']),
});

export const getEmptyJobFinancials = (): JobFinancials => jobFinancialsFromDto({
  jobId: void 0,
  invoices: [],
  jobNumber: void 0,
  customerId: void 0,
  jobExpenses: [],
  siteAddress: void 0,
  customerName: void 0,
  revisedBudget: void 0,
  contractAmount: void 0,
  variationsAmount: void 0,
  reportUrl: void 0,
});

export type JobFinancialsDrawListFilter = {
  code: string;
  value: boolean;
};

export type JobFinancialsExpenseListFilter = {
  code: string;
  value: boolean;
};

export enum JobFinancialsInvoicePlacement {
  Regular = 'Regular',
  TotalContract = 'TotalContract',
  TotalChangeOrders = 'TotalChangeOrders',
  TotalDraws = 'TotalDraws',
  TotalCrNotes = 'TotalCrNotes',
}

export enum JobFinancialsExpensePlacement {
  Total = 'Total',
}

function calculateTotal(items: Object[], property: string): number {
  return coalesce<number>(sumBy(items, property), 0);
}

function createTotalContractInvoice(invoices: JobFinancialsDraw[],
                                    placement: JobFinancialsInvoicePlacement): JobFinancialsDrawTotal {
  const totalAmount = coalesce<number>(sumBy(filter(invoices, { type: InvoiceType.Draw }), 'amountWithGst'), 0);
  const totalBalance = coalesce<number>(sumBy(filter(invoices, { type: InvoiceType.Draw }), 'balance'), 0);

  return <JobFinancialsDrawTotal>{
    invoiceId: `total:${placement}`,
    amountWithGst: totalAmount,
    balance: totalBalance,
    placement,
  };
}

function createTotalChangeOrdersInvoice(invoices: JobFinancialsDraw[],
                                        placement: JobFinancialsInvoicePlacement): JobFinancialsDrawTotal {
  const totalAmount = coalesce<number>(sumBy(filter(invoices, { type: InvoiceType.ChangeOrder }), 'amountWithGst'), 0);
  const totalBalance = coalesce<number>(sumBy(filter(invoices, { type: InvoiceType.ChangeOrder }), 'balance'), 0);

  return <JobFinancialsDrawTotal>{
    invoiceId: `total:${placement}`,
    amountWithGst: totalAmount,
    balance: totalBalance,
    placement,
  };
}

function createTotalDrawsInvoice(invoices: JobFinancialsDraw[],
                                 placement: JobFinancialsInvoicePlacement): JobFinancialsDrawTotal {
  const invoiceList = filter(invoices, ({ type }: JobFinancialsDraw) =>
    includes([InvoiceType.Draw, InvoiceType.ChangeOrder], type));
  const totalAmount = coalesce<number>(sumBy(invoiceList, 'amountWithGst'), 0);
  const totalBalance = coalesce<number>(sumBy(invoiceList, 'balance'), 0);

  return <JobFinancialsDrawTotal>{
    invoiceId: `total:${placement}`,
    amountWithGst: totalAmount,
    balance: totalBalance,
    placement,
  };
}

function createTotalCrNotesInvoice(invoices: JobFinancialsDraw[],
                                   placement: JobFinancialsInvoicePlacement): JobFinancialsDrawTotal {
  const totalAmount = calculateTotal(filter(invoices, { type: InvoiceType.CreditNote }), 'amountWithGst');
  const totalBalance = calculateTotal(filter(invoices, { type: InvoiceType.CreditNote }), 'balance');

  return <JobFinancialsDrawTotal>{
    invoiceId: `total:${placement}`,
    amountWithGst: totalAmount,
    balance: totalBalance,
    placement,
  };
}

export const calculateDrawTotals = (invoices: JobFinancialsDraw[]) => [
  createTotalContractInvoice(invoices, JobFinancialsInvoicePlacement.TotalContract),
  createTotalChangeOrdersInvoice(invoices, JobFinancialsInvoicePlacement.TotalChangeOrders),
  createTotalDrawsInvoice(invoices, JobFinancialsInvoicePlacement.TotalDraws),
  createTotalCrNotesInvoice(invoices, JobFinancialsInvoicePlacement.TotalCrNotes),
];

function createTotalExpense(expenses: JobFinancialsExpense[],
                            placement: JobFinancialsExpensePlacement): JobFinancialsExpenseTotals {
  const totalBudget = calculateTotal(expenses, 'budgetCost');
  const totalActual = calculateTotal(expenses, 'actualCost');
  const totalVariance = calculateTotal(expenses, 'variance');

  return <JobFinancialsExpenseTotals>{
    costCenterId: `total:${placement}`,
    actualCost: totalActual,
    budgetCost: totalBudget,
    variance: totalVariance,
    placement,
  };
}

export const calculateExpenseTotals = (expenses: JobFinancialsExpense[]) => [
  createTotalExpense(expenses, JobFinancialsExpensePlacement.Total),
];

export const jobDrawsInvoiceNumberComparator = valueComparator('invoiceNumber');

export function buildJobFinancialsTradingAccount(jobFinancials: JobFinancials): JobFinancialsTradingAccount {
  const estimateCost = jobFinancials.revisedBudget || 0;
  const invoicesAmount = jobFinancials.invoicesAmount || 0;
  const contractAmount = jobFinancials.contractAmount || 0;
  const variationsAmount = jobFinancials.variationsAmount || 0;

  const revisedBudget = estimateCost;
  const lessTotalInvoices = calculateTotal(jobFinancials.jobExpenses, 'actualCost');
  const variance = revisedBudget - lessTotalInvoices;
  const estimatedProfit = contractAmount  + variationsAmount - estimateCost;
  const actualProfitToDate = invoicesAmount - lessTotalInvoices;
  const originalContract = contractAmount;
  const plusVariations = variationsAmount;
  const currentContractPrice = originalContract + plusVariations;
  const lessClaimsPaid = invoicesAmount;
  const balanceToReceive = currentContractPrice - lessClaimsPaid;
  return {
    revisedBudget,
    lessTotalInvoices,
    variance,
    estimatedProfit,
    actualProfitToDate,
    originalContract,
    plusVariations,
    currentContractPrice,
    lessClaimsPaid,
    balanceToReceive,
  };
}

export const EMPTY_JOB_FINANCIALS_TRADING_ACCOUNT: JobFinancialsTradingAccount = {
  revisedBudget: 0,
  lessTotalInvoices: 0,
  variance: 0,
  estimatedProfit: 0,
  actualProfitToDate: 0,
  originalContract: 0,
  plusVariations: 0,
  currentContractPrice: 0,
  lessClaimsPaid: 0,
  balanceToReceive: 0,
};

export enum JobFinancialsTabs {
  Draws = 'DRAWS',
  Expenses = 'EXPENSES',
  TradingAccount = 'TRADING_ACCOUNT',
}

export enum JobFinancialsDrawsFilterCode {
  isOnlyActiveDraws = 'isOnlyActiveDrawsFilter',
  isAllDraws = 'isAllDrawsFilter',
}

export enum JobFinancialsExpensesFilterCode {
  isAllExpenses = 'isAllExpensesFilter',
  isNonZeroExpenses = 'isNonZeroExpensesFilter',
  isActualBudgetExpenses = 'isActualBudgetExpensesFilter',
}
